import { Tooltip } from 'antd';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Spotlight } from 'api/spotlight/types';
import { Avatar } from 'components/atoms/avatar';
import { SpotlightMessage20 } from 'components/atoms/icons/spotlight-message';
import { Popover } from 'components/atoms/popover';
import { ProductiveHeading07 } from 'components/styles';
import { useGetUserByUserId } from 'hooks/use-idb';
import { fullName } from 'utils/full-name';
import {
  TaskNameWithCke,
  TaskNameWithCkeExpanded,
} from '../templates/commons/name-field';

const Content = styled.span``;

const Tag = styled.div`
  display: flex;
  width: fit-content;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-03) var(--spacing-04);
  gap: var(--spacing-03);
  border: 1px solid #f9d7af;
  cursor: pointer;
  position: relative;
  height: 32px;
  background: white;
  border-radius: 100px;
  margin-bottom: var(--spacing-02);

  &:before {
    width: 8px;
    height: 8px;
    border: 6px solid;
    left: 0px;
    bottom: 4px;
    border-bottom-color: #f9d7af;
    border-bottom-width: 10px;
  }
  &:after {
    width: 8px;
    height: 8px;
    border: 5px solid;
    left: 1.5px;
    bottom: 4.5px;
    border-bottom-color: #ffffff;
    border-bottom-width: 9px;
  }
  &:before,
  &:after {
    content: '';
    display: block;
    position: absolute;
    z-index: 1;
    border-top-color: transparent;
    border-left-color: transparent;
    border-right-color: transparent;
    transform: rotateZ(219deg);
    transform-origin: bottom;
  }
  :hover {
    background: #f9d7af;
    &:after {
      border-bottom-color: #f9d7af;
    }
  }
`;

const CountTag = styled(Tag)`
  &:before,
  &:after {
    display: none;
  }
`;

export const SpotlightInTag: React.FC<{
  spotlight: Spotlight;
  onSpotlightClick: (s: Spotlight) => void;
}> = ({ spotlight, onSpotlightClick }) => {
  const user = useGetUserByUserId(spotlight?.createdBy);

  return (
    <Tag onClick={() => onSpotlightClick(spotlight)}>
      <SpotlightMessage20 />
      {user ? (
        <Avatar size="small" src={user?.photoUrl} name={fullName(user)} />
      ) : (
        <></>
      )}
      <Content>
        <TaskNameWithCke taskName={spotlight?.caption} />
      </Content>
    </Tag>
  );
};

const StyledAvatar = styled(Avatar)``;

const UserNameInMessageTooltip = styled.div`
  ${ProductiveHeading07};
  /* ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;
const DateInMessageTooltip = styled.div`
  font-size: var(--font-size-base);
  opacity: 0.4;
`;

const SpotlightAsMessageWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  border-radius: var(--border-radius-small);
  max-width: 350px;
  gap: var(--spacing-04);
  padding: var(--spacing-05);
  background: #fef7f0;
`;

const SpotlightContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
  width: 100%;
`;

export const SpotlightAsMessage: React.FC<{
  spotlight: Spotlight;
  onSpotlightClick: (s: Spotlight) => void;
}> = ({ spotlight, onSpotlightClick }) => {
  const user = useGetUserByUserId(spotlight?.createdBy);

  const formattedDate = useMemo(() => {
    const createdAt = moment(spotlight?.createdAt);

    if (createdAt.isSame(moment(), 'year')) {
      return createdAt.format('DD MMM, HH:mm A');
    }

    return createdAt.format('DD MMM YY, HH:mm A');
  }, [spotlight?.createdAt]);

  return (
    <SpotlightAsMessageWrapper onClick={() => onSpotlightClick(spotlight)}>
      {user ? (
        <StyledAvatar
          shape="square"
          size="large"
          src={user?.photoUrl}
          name={fullName(user)}
        />
      ) : (
        <></>
      )}
      <SpotlightContentWrapper>
        {user ? (
          <Tooltip title={fullName(user)}>
            <UserNameInMessageTooltip>
              {fullName(user)}
            </UserNameInMessageTooltip>
          </Tooltip>
        ) : (
          <div />
        )}
        <Content>
          <>
            <TaskNameWithCkeExpanded taskName={spotlight?.caption} />
          </>
        </Content>
        <DateInMessageTooltip>{formattedDate}</DateInMessageTooltip>
      </SpotlightContentWrapper>
    </SpotlightAsMessageWrapper>
  );
};

const SpotlightGroupWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-02);
`;

const SpotlightMessageWrapperInList = styled.div`
  /* padding: var(--spacing-05) var(--spacing-04); */
  background: #fef7f0;
  ${SpotlightAsMessageWrapper} {
    position: relative;
    &:after {
      content: '';
      border-bottom: 1px solid var(--border-color);
      position: absolute;
      bottom: 0;
      left: var(--spacing-05);
      right: var(--spacing-05);
    }
    border-radius: 0;
    &:hover {
      background-color: #feeee2;
    }
  }
  ${SpotlightAsMessageWrapper}:last-child {
    &:after {
      content: none;
    }
  }
`;

export const SpotlightMessageListPopover = styled(Popover)`
  max-height: 70vh;
  .ant-popover-inner-content {
    max-height: 70vh;
    overflow-y: auto;
  }
`;

export const SpotlightGroup: React.FC<{
  spotlights: Spotlight[];
  onSpotlightClick: (s: Spotlight) => void;
}> = ({ spotlights, onSpotlightClick }) => {
  // a number that says how maby spotlights are rendered as tags and how many are rendered as messages.
  // the number shoule be based on the width of the container.
  // if the container is too small, then all spotlights should be rendered as messages.
  // if the container is big enough, then count number of spotlights should be rendered as tags and the rest as messages.
  // if the container is big enough, then all spotlights should be rendered as tags.
  const [count, setCount] = useState(2);

  const firstTwoSpotlights = useMemo(
    () => (spotlights.length > count ? spotlights.slice(0, count) : spotlights),
    [count, spotlights],
  );

  const remainingSpotlights = useMemo(
    () => (spotlights.length > count ? spotlights.slice(count) : []),
    [count, spotlights],
  );

  return (
    <SpotlightGroupWrapper
      ref={(_ref) => {
        setCount(Math.max((_ref?.clientWidth || 0) / 340, 1));
      }}
    >
      {firstTwoSpotlights.map((spotlight) => (
        <SpotlightMessageListPopover
          getPopupContainer={() =>
            document.querySelector('.rl-cp-page') || document.body
          }
          placement="bottomLeft"
          overlayInnerStyle={{ borderRadius: 'var(--border-radius-small)' }}
          // destroyTooltipOnHide
          content={
            <SpotlightAsMessage
              onSpotlightClick={onSpotlightClick}
              key={spotlight?.customerResourcePinnedMappingId}
              spotlight={spotlight}
            />
          }
        >
          <div>
            <SpotlightInTag
              onSpotlightClick={onSpotlightClick}
              key={spotlight?.customerResourcePinnedMappingId}
              spotlight={spotlight}
            />
          </div>
        </SpotlightMessageListPopover>
      ))}
      {remainingSpotlights.length > 0 ? (
        <SpotlightMessageListPopover
          getPopupContainer={() =>
            document.querySelector('.rl-cp-page') || document.body
          }
          overlayInnerStyle={{ borderRadius: 'var(--border-radius-small)' }}
          // destroyTooltipOnHide
          placement="bottomLeft"
          content={
            <SpotlightMessageWrapperInList>
              {remainingSpotlights.map((spotlight) => (
                <SpotlightAsMessage
                  onSpotlightClick={onSpotlightClick}
                  key={spotlight?.customerResourcePinnedMappingId}
                  spotlight={spotlight}
                />
              ))}
            </SpotlightMessageWrapperInList>
          }
        >
          <span>
            <CountTag>+{remainingSpotlights.length}</CountTag>
          </span>
        </SpotlightMessageListPopover>
      ) : (
        <></>
      )}
    </SpotlightGroupWrapper>
  );
};
